var { initIframePrint } = require('./shared/iframe-print');

/**
 * Quick Notes - Simple Note-Taking Tool
 * Handles auto-save, character counting, and quick text insertion
 */

(function () {
    'use strict';

    const titleInput = document.getElementById('note-title');
    const editor = document.getElementById('notes-editor');
    const saveStatus = document.getElementById('save-status');
    const charCount = document.getElementById('char-count');

    let saveTimeout = null;
    const STORAGE_KEY = 'quick-notes-data';

    /**
     * Load saved notes from localStorage
     */
    function loadNotes() {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const data = JSON.parse(saved);
                titleInput.value = data.title || '';
                editor.value = data.content || '';
                updateCharCount();
            } catch (e) {
                console.error('Error loading notes:', e);
            }
        }
    }

    /**
     * Save notes to localStorage
     */
    function saveNotes() {
        const data = {
            title: titleInput.value,
            content: editor.value,
            savedAt: new Date().toISOString()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

        saveStatus.innerHTML = '<i class="bi bi-check-circle text-success"></i> Saved';
    }

    /**
     * Debounced save - waits 500ms after user stops typing
     */
    function scheduleSave() {
        saveStatus.innerHTML = '<i class="bi bi-three-dots text-muted"></i> Saving...';

        if (saveTimeout) clearTimeout(saveTimeout);
        saveTimeout = setTimeout(saveNotes, 500);
    }

    /**
     * Update character and word count display
     */
    function updateCharCount() {
        const count = editor.value.length;
        const words = editor.value.trim() ? editor.value.trim().split(/\s+/).length : 0;
        charCount.textContent = `${count.toLocaleString()} characters - ${words.toLocaleString()} words`;
    }

    /**
     * Handle copy all button click
     */
    async function handleCopyAll() {
        try {
            await navigator.clipboard.writeText(editor.value);
            const btn = document.getElementById('btn-copy');
            const originalHTML = btn.innerHTML;
            btn.innerHTML = '<i class="bi bi-check me-2"></i>Copied!';
            btn.classList.add('btn-success');
            btn.classList.remove('btn-outline-secondary');

            setTimeout(() => {
                btn.innerHTML = originalHTML;
                btn.classList.remove('btn-success');
                btn.classList.add('btn-outline-secondary');
            }, 2000);
        } catch (err) {
            const btn = document.getElementById('btn-copy');
            const originalHTML = btn.innerHTML;
            btn.innerHTML = '<i class="bi bi-x-lg me-2"></i>Copy failed';
            btn.classList.add('btn-danger');
            btn.classList.remove('btn-outline-secondary');

            setTimeout(() => {
                btn.innerHTML = originalHTML;
                btn.classList.remove('btn-danger');
                btn.classList.add('btn-outline-secondary');
            }, 2000);
        }
    }

    /**
     * Perform the actual clear action
     */
    function performClear() {
        editor.value = '';
        titleInput.value = '';
        saveNotes();
        updateCharCount();
    }

    /**
     * Handle clear button click - opens the confirmation dialog
     */
    function handleClear() {
        openClearConfirmation(document.getElementById('clearConfirmationOverlay'));
    }

    /**
     * Initialize clear confirmation modal controls
     */
    function initClearConfirmation() {
        const clearButton = document.getElementById('btn-clear');
        const overlay = document.getElementById('clearConfirmationOverlay');
        if (!clearButton || !overlay) {
            return;
        }

        const closeButtons = overlay.querySelectorAll('[data-clear-confirmation="close"]');
        const confirmButton = document.getElementById('clearConfirmationConfirm');

        clearButton.addEventListener('click', function () {
            openClearConfirmation(overlay);
        });

        closeButtons.forEach(function (button) {
            button.addEventListener('click', function () {
                closeClearConfirmation(overlay);
            });
        });

        if (confirmButton) {
            confirmButton.addEventListener('click', function () {
                performClear();
                closeClearConfirmation(overlay);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }
    }

    /**
     * Open the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function openClearConfirmation(overlay) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = scrollbarWidth + 'px';
        }
        overlay.classList.add('is-visible');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('clear-confirmation-open');
    }

    /**
     * Close the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function closeClearConfirmation(overlay) {
        overlay.classList.remove('is-visible');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('clear-confirmation-open');
        document.body.style.paddingRight = '';
    }

    /**
     * Calculate the number of characters needed for a full-width divider
     * Uses binary search to find the exact number that fits on one line
     * @returns {number} Number of divider characters
     */
    function calculateDividerLength() {
        const dividerChar = '\u2500';
        
        // Create a hidden textarea clone to measure text wrapping
        const measureDiv = document.createElement('div');
        const styles = window.getComputedStyle(editor);
        
        // Copy all relevant styles from the textarea
        measureDiv.style.cssText = `
            position: absolute;
            visibility: hidden;
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            font: ${styles.font};
            font-family: ${styles.fontFamily};
            font-size: ${styles.fontSize};
            line-height: ${styles.lineHeight};
            letter-spacing: ${styles.letterSpacing};
            padding: ${styles.padding};
            border: ${styles.border};
            box-sizing: ${styles.boxSizing};
            width: ${editor.clientWidth}px;
        `;
        
        document.body.appendChild(measureDiv);
        
        // Binary search for the maximum characters that fit on one line
        let low = 1;
        let high = 200;
        let result = 20; // Safe default
        
        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            measureDiv.textContent = dividerChar.repeat(mid);
            
            // Check if text wraps (scrollHeight > one line height)
            const singleLineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.5;
            const contentHeight = measureDiv.scrollHeight - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom);
            
            if (contentHeight <= singleLineHeight + 2) {
                // Fits on one line, try more characters
                result = mid;
                low = mid + 1;
            } else {
                // Wraps to multiple lines, try fewer characters
                high = mid - 1;
            }
        }
        
        document.body.removeChild(measureDiv);
        
        // Subtract a small buffer to ensure it never wraps
        return Math.max(result - 2, 20);
    }

    /**
     * Handle quick insert button clicks
     * @param {string} type - Type of insert (date, time, bullet, checkbox, divider)
     */
    function handleQuickInsert(type) {
        let text = '';
        const start = editor.selectionStart;
        const end = editor.selectionEnd;
        
        // Check if we're at the beginning of a line or need a newline
        const beforeCursor = editor.value.substring(0, start);
        const needsNewline = beforeCursor.length > 0 && !beforeCursor.endsWith('\n');

        switch (type) {
            case 'date':
                text = new Date().toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
                break;
            case 'time':
                text = new Date().toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
                break;
            case 'bullet':
                // Unicode bullet point U+2022
                text = (needsNewline ? '\n' : '') + '\u2022 ';
                break;
            case 'checkbox':
                // Unicode ballot box U+2610
                text = (needsNewline ? '\n' : '') + '\u2610 ';
                break;
            case 'divider':
                // Unicode box drawing horizontal U+2500
                // Calculate divider length based on textarea width
                const dividerChar = '\u2500';
                const charWidth = calculateDividerLength();
                let dividerLine = '';
                for (let i = 0; i < charWidth; i++) {
                    dividerLine += dividerChar;
                }
                text = (needsNewline ? '\n' : '') + dividerLine + '\n';
                break;
        }

        editor.value = editor.value.substring(0, start) + text + editor.value.substring(end);
        editor.selectionStart = editor.selectionEnd = start + text.length;
        editor.focus();

        scheduleSave();
        updateCharCount();
    }

    /**
     * Initialize the application
     */
    function init() {
        initIframePrint({
            formId: 'qnPrintForm',
            iframeId: 'qnPrintFrame',
            buttonId: 'qnPrintBtn',
            readyLabel: '<i class="bi bi-printer me-2" aria-hidden="true"></i>Print Notes',
        });

        // Load saved notes
        loadNotes();
        updateCharCount();

        // Event listeners for auto-save
        editor.addEventListener('input', () => {
            scheduleSave();
            updateCharCount();
        });

        titleInput.addEventListener('input', scheduleSave);

        // Button event listeners
        document.getElementById('btn-copy').addEventListener('click', handleCopyAll);

        initClearConfirmation();

        // Quick insert tool buttons
        document.querySelectorAll('.quick-tool-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const type = btn.dataset.insert;
                handleQuickInsert(type);
            });
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Expose public API (if needed for testing or external access)
    window.QuickNotes = {
        save: saveNotes,
        load: loadNotes,
        clear: performClear
    };

})();
